import { useState, useEffect, useRef } from 'react';
import { Download, Calendar, User, Filter, TrendingUp, Users, Clock, Target, BarChart3, Activity, PieChart as PieChartIcon, Bell } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { getAnalyticsSummary, exportAnalyticsCSV, getAnalyticsFilterSuggestions } from '../api';

function ReportTypeahead({ field, label, Icon, value, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const requestId = useRef(0);

  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    const currentRequest = ++requestId.current;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      try {
        const results = await getAnalyticsFilterSuggestions(field, value, controller.signal);
        if (requestId.current === currentRequest) {
          setItems(results);
          setHighlightedIndex(-1);
        }
      } catch (error) {
        if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
          console.error(`Unable to load ${field} suggestions`, error);
          if (requestId.current === currentRequest) setItems([]);
        }
      } finally {
        if (requestId.current === currentRequest) setLoading(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [field, isOpen, value]);

  const selectItem = (item) => {
    onChange(item);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((index) => Math.min(index + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      selectItem(items[highlightedIndex]);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const listId = `${field}-suggestions`;
  return (
    <div className="relative">
      <label className="block text-sm font-medium text-textLight/70 mb-2">
        <Icon size={14} className="inline mr-1" />
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(event) => { onChange(event.target.value); setIsOpen(true); }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => window.setTimeout(() => setIsOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-activedescendant={highlightedIndex >= 0 ? `${listId}-${highlightedIndex}` : undefined}
        className="w-full px-3 py-2 bg-navy border border-teal/30 rounded-md text-textLight placeholder-textLight/40 focus:outline-none focus:ring-2 focus:ring-teal"
      />
      {isOpen && (
        <div id={listId} role="listbox" className="absolute z-30 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-teal/40 bg-navy-dark shadow-xl">
          {loading && <div className="px-3 py-2 text-sm text-textLight/70">Loading suggestions…</div>}
          {!loading && items.length === 0 && <div className="px-3 py-2 text-sm text-textLight/70">No suggestions found. You can still use this text.</div>}
          {!loading && items.map((item, index) => (
            <button
              key={item}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              aria-selected={highlightedIndex === index}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectItem(item)}
              className={`block w-full px-3 py-2 text-left text-sm ${highlightedIndex === index ? 'bg-teal text-white' : 'text-textLight hover:bg-navy'}`}
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Reports() {
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    recruiter_name: '',
    role: ''
  });
  
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRangeError, setDateRangeError] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Date range validation (≤30 days)
  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      const start = new Date(filters.start_date);
      const end = new Date(filters.end_date);
      const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      
      if (diffDays > 30) {
        setDateRangeError('Date range cannot exceed 30 days');
      } else if (diffDays < 0) {
        setDateRangeError('End date must be after start date');
      } else {
        setDateRangeError(null);
      }
    } else {
      setDateRangeError(null);
    }
  }, [filters.start_date, filters.end_date]);
  
  // Fetch analytics data on mount and filter change
  useEffect(() => {
    fetchAnalytics();
  }, []);
  
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getAnalyticsSummary(filters);
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.detail || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const handleApplyFilters = () => {
    if (dateRangeError) {
      return;
    }
    fetchAnalytics();
  };
  
  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      recruiter_name: '',
      role: ''
    });
    setDateRangeError(null);
  };
  
  const handleExport = async () => {
    if (dateRangeError) {
      alert('Please fix date range error before exporting');
      return;
    }
    
    setExporting(true);
    try {
      await exportAnalyticsCSV(filters);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export CSV: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };
  
  // Calculate KPI values
  const kpiData = analyticsData ? {
    totalCalls: analyticsData.total_calls || 0,
    conversionRate: analyticsData.conversion_metrics?.length > 0
      ? (analyticsData.conversion_metrics.reduce((sum, m) => sum + m.conversion_ratio_percent, 0) / analyticsData.conversion_metrics.length).toFixed(2)
      : 0,
    followUpRate: analyticsData.follow_up_rate || 0,
    avgResponseTime: analyticsData.avg_time_to_response
      ? `${analyticsData.avg_time_to_response.hours}h (${analyticsData.avg_time_to_response.days}d)`
      : 'N/A'
  } : null;
  
  // Prepare chart data for recruiter-wise bar chart
  const recruiterChartData = analyticsData?.conversion_metrics?.map(m => ({
    name: m.name || 'Unknown',
    submissions: m.total_submitted || 0,
    shortlisted: m.shortlisted || 0,
    conversion: m.conversion_ratio_percent || 0
  })) || [];
  
  // Prepare chart data for weekly submissions line chart
  const weeklyChartData = analyticsData?.weekly_submissions?.map(w => ({
    name: w.name || 'Unknown',
    submissions: w.count || 0
  })) || [];
  
  // Task 12: Prepare outcome distribution pie chart data
  const outcomeChartData = analyticsData?.outcome_distribution 
    ? Object.entries(analyticsData.outcome_distribution).map(([name, value]) => ({
        name: name || 'Not Specified',
        value: value
      }))
    : [];
  
  const COLORS = ['#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#10b981'];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gold">Analytics & Reports</h1>
          <p className="text-textLight/70 mt-1">Admin-level recruiter performance insights</p>
        </div>
        <button
          onClick={handleExport}
          disabled={dateRangeError || exporting}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
            dateRangeError || exporting
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-teal text-white hover:bg-teal-light shadow-lg shadow-teal/50'
          }`}
        >
          <Download size={18} />
          <span>{exporting ? 'Exporting...' : 'Export CSV'}</span>
        </button>
      </div>
      
      {/* Filters Panel */}
      <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="text-teal" size={20} />
          <h2 className="text-xl font-heading font-semibold text-textLight">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-textLight/70 mb-2">
              <Calendar size={14} className="inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 bg-navy border border-teal/30 rounded-md text-textLight focus:outline-none focus:ring-2 focus:ring-teal"
            />
          </div>
          
          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-textLight/70 mb-2">
              <Calendar size={14} className="inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 bg-navy border border-teal/30 rounded-md text-textLight focus:outline-none focus:ring-2 focus:ring-teal"
            />
          </div>
          
          <ReportTypeahead field="recruiter_name" label="Recruiter Name" Icon={User} value={filters.recruiter_name} onChange={(value) => handleFilterChange('recruiter_name', value)} placeholder="Search by name..." />
          
          <ReportTypeahead field="role" label="Candidate Role" Icon={Target} value={filters.role} onChange={(value) => handleFilterChange('role', value)} placeholder="Filter by role..." />
        </div>
        
        {/* Date Range Error */}
        {dateRangeError && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
            <p className="text-red-400 text-sm">{dateRangeError}</p>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex space-x-3 mt-4">
          <button
            onClick={handleApplyFilters}
            disabled={dateRangeError || loading}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              dateRangeError || loading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-teal text-white hover:bg-teal-light shadow-lg shadow-teal/50'
            }`}
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 bg-navy-light text-textLight rounded-lg font-medium hover:bg-navy border border-teal/30 transition-all"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}
      
      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal mx-auto"></div>
          <p className="text-textLight/70 mt-4">Loading analytics data...</p>
        </div>
      )}
      
      {/* KPI Cards */}
      {!loading && analyticsData && kpiData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Calls */}
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl hover:shadow-teal/30 transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-textLight/70">Total Calls</h3>
                <Users className="text-teal" size={24} />
              </div>
              <p className="text-3xl font-bold text-gold">{kpiData.totalCalls}</p>
              <p className="text-xs text-textLight/50 mt-1">All call logs</p>
            </div>
            
            {/* Conversion Rate */}
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl hover:shadow-teal/30 transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-textLight/70">Conversion Rate</h3>
                <TrendingUp className="text-teal" size={24} />
              </div>
              <p className="text-3xl font-bold text-gold">{kpiData.conversionRate}%</p>
              <p className="text-xs text-textLight/50 mt-1">Avg. shortlisted ratio</p>
            </div>
            
            {/* Follow-up Rate */}
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl hover:shadow-teal/30 transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-textLight/70">Follow-up Rate</h3>
                <Activity className="text-teal" size={24} />
              </div>
              <p className="text-3xl font-bold text-gold">{kpiData.followUpRate}%</p>
              <p className="text-xs text-textLight/50 mt-1">Calls with follow-up</p>
            </div>
            
            {/* Avg Response Time */}
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl hover:shadow-teal/30 transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-textLight/70">Avg Response Time</h3>
                <Clock className="text-teal" size={24} />
              </div>
              <p className="text-2xl font-bold text-gold">{kpiData.avgResponseTime}</p>
              <p className="text-xs text-textLight/50 mt-1">Time to first call</p>
            </div>
          </div>
          
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart: Recruiter-wise Performance */}
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl">
              <div className="flex items-center space-x-2 mb-4">
                <BarChart3 className="text-teal" size={20} />
                <h2 className="text-xl font-heading font-semibold text-textLight">Recruiter Performance</h2>
              </div>
              
              {recruiterChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={recruiterChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
                    <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e3a5f',
                        border: '1px solid #14b8a6',
                        borderRadius: '8px',
                        color: '#e2e8f0'
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#94a3b8' }} />
                    <Bar dataKey="submissions" fill="#14b8a6" name="Total Submissions" />
                    <Bar dataKey="shortlisted" fill="#f59e0b" name="Shortlisted" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-300 flex items-center justify-center text-textLight/50">
                  No data available
                </div>
              )}
            </div>
            
            {/* Line Chart: Weekly Trend */}
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl">
              <div className="flex items-center space-x-2 mb-4">
                <Activity className="text-teal" size={20} />
                <h2 className="text-xl font-heading font-semibold text-textLight">Weekly Submissions</h2>
              </div>
              
              {weeklyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={weeklyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
                    <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e3a5f',
                        border: '1px solid #14b8a6',
                        borderRadius: '8px',
                        color: '#e2e8f0'
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#94a3b8' }} />
                    <Line type="monotone" dataKey="submissions" stroke="#14b8a6" strokeWidth={2} name="Submissions" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-300 flex items-center justify-center text-textLight/50">
                  No data available
                </div>
              )}
            </div>
          </div>
          
          {/* Task 12: Outcome Breakdown Pie Chart */}
          {outcomeChartData.length > 0 && (
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl">
              <div className="flex items-center space-x-2 mb-4">
                <PieChartIcon className="text-teal" size={20} />
                <h2 className="text-xl font-heading font-semibold text-textLight">Call Outcome Breakdown</h2>
              </div>
              
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={outcomeChartData}
                    cx="50%"
                    cy="50%"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {outcomeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e3a5f',
                      border: '1px solid #14b8a6',
                      borderRadius: '8px',
                      color: '#e2e8f0'
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Conversion Metrics Table */}
          {analyticsData.conversion_metrics && analyticsData.conversion_metrics.length > 0 && (
            <div className="bg-navy-dark p-6 rounded-lg border border-teal/30 shadow-xl">
              <h2 className="text-xl font-heading font-semibold text-textLight mb-4">Detailed Conversion Metrics</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-teal/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-textLight/70">Recruiter</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-textLight/70">Email</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-textLight/70">Total Submitted</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-textLight/70">Shortlisted</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-textLight/70">Conversion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.conversion_metrics.map((metric, idx) => (
                      <tr key={idx} className="border-b border-teal/10 hover:bg-navy-light transition-colors">
                        <td className="py-3 px-4 text-textLight">{metric.name}</td>
                        <td className="py-3 px-4 text-textLight/70 text-sm">{metric.email}</td>
                        <td className="py-3 px-4 text-center text-gold font-semibold">{metric.total_submitted}</td>
                        <td className="py-3 px-4 text-center text-teal font-semibold">{metric.shortlisted}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            metric.conversion_ratio_percent >= 50 ? 'bg-green-500/20 text-green-400' :
                            metric.conversion_ratio_percent >= 25 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {metric.conversion_ratio_percent}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Empty State */}
      {!loading && !error && !analyticsData && (
        <div className="text-center py-12">
          <BarChart3 className="mx-auto text-textLight/30" size={64} />
          <p className="text-textLight/70 mt-4">No analytics data available</p>
          <p className="text-textLight/50 text-sm mt-2">Apply filters and click "Apply Filters" to view data</p>
        </div>
      )}
    </div>
  );
}

export default Reports;
